import { createElement } from 'react';
import { EXPORT } from '#shared/core/card-geometry';
import { TIER_SHADOW } from '#shared/core/ratings';
import { type FigureArt, planArt } from './art';
import { CardSvg } from './CardSvg';
import { copy } from './copy';
import { fontFaceCss, type Subset, subsetsFor } from './fonts';
import {
  cardAriaLabel,
  cardOvr,
  cardState,
  cardTier,
  jerseyText,
  largeAttrs,
  plateName,
  subline,
  tierLabel,
} from './model';
import type { PlayerCardProps } from './PlayerCard';
import { revealFlags } from './reveal';
import { buildSticker, measureSticker } from './sticker';

/**
 * PNG export and the sticker bake. Every module that needs a picture of an SVG calls these,
 * nobody ships a second renderer.
 *
 * The recipe, and why: an SVG drawn as an image has no access to the page, so the markup is made
 * self-contained first. Pictures become data URIs, the two Fira Sans faces travel inside as
 * @font-face data URIs, the root loses its CSS sizing, and there is no `foreignObject` and no
 * filter, which keeps Safari from tainting the canvas. Then the SVG is decoded as an image and
 * drawn to a canvas of exactly width x height. The first draw only warms the fonts up (Safari and
 * Chrome both draw a font-less first frame), the second one is the picture.
 */

export { buildSticker } from './sticker';

export interface ExportPngOptions {
  width: number;
  height: number;
  /** For the download fallback of `shareImage`. */
  fileName?: string;
}

export const CARD_EXPORT_SIZE = { width: EXPORT.w, height: EXPORT.h } as const;

/** Sticker height of the PNG export: the figure is about 981 px tall there, so 1100 is never upscaled. */
export const EXPORT_STICKER_H = 1100;

/* ---------- pure markup helpers (tested without a browser) ---------- */

const IMAGE_TAG = /<image\b[^>]*>/g;
const HREF_ATTR = /\b(xlink:href|href)="([^"]*)"/;

/** URLs of every `<image>` that is not a data URI yet. */
export function findImageHrefs(markup: string): string[] {
  const out = new Set<string>();
  for (const tag of markup.match(IMAGE_TAG) ?? []) {
    const m = HREF_ATTR.exec(tag);
    const href = m?.[2];
    if (href && !href.startsWith('data:')) out.add(decodeEntities(href));
  }
  return [...out];
}

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");
}

/** Swap `<image>` hrefs for the data URIs in `map` (keyed by the decoded URL). */
export function inlineImageHrefs(markup: string, map: ReadonlyMap<string, string>): string {
  return markup.replace(IMAGE_TAG, (tag) =>
    tag.replace(HREF_ATTR, (whole, attr: string, href: string) => {
      const data = map.get(decodeEntities(href));
      return data ? `${attr}="${data}"` : whole;
    }),
  );
}

/**
 * Make markup safe to draw as a standalone image: namespace on the root, explicit pixel size, no
 * root `style` (a `width:100%` there would override the size), and the font CSS inside a style
 * element. The viewBox stays, so the card scales to the canvas.
 */
export function prepareSvgMarkup(markup: string, o: { width: number; height: number; css?: string }): string {
  const open = /<svg\b[^>]*>/.exec(markup);
  if (!open) throw new Error('cards.exportPng: not an SVG');
  let tag = open[0];
  tag = tag.replace(/\s(width|height|style)="[^"]*"/g, '');
  if (!/\sxmlns="/.test(tag)) tag = tag.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  if (/xlink:href/.test(markup) && !/xmlns:xlink=/.test(tag))
    tag = tag.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
  tag = tag.replace('<svg', `<svg width="${o.width}" height="${o.height}"`);
  const style = o.css ? `<style>${o.css}</style>` : '';
  return markup.slice(0, open.index) + tag + style + markup.slice(open.index + open[0].length);
}

/** Text inside the markup, for choosing font subsets. */
export function textOf(markup: string): string {
  return Array.from(markup.matchAll(/>([^<>]+)</g), (m) => m[1] ?? '').join(' ');
}

/* ---------- browser parts ---------- */

const dataUriCache = new Map<string, Promise<string>>();

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('cards: could not read a file'));
    r.readAsDataURL(blob);
  });
}

/** Fetch a file (same origin, cookie sent) and return it as a data URI. Font files are cached. */
export async function fileToDataUri(url: string): Promise<string> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cards: ${url} answered ${res.status}`);
  return blobToDataUri(await res.blob());
}

function cachedDataUri(url: string): Promise<string> {
  const hit = dataUriCache.get(url);
  if (hit) return hit;
  const p = fileToDataUri(url);
  dataUriCache.set(url, p);
  p.catch(() => dataUriCache.delete(url));
  return p;
}

async function decodeSvg(markup: string): Promise<{ img: HTMLImageElement; done: () => void }> {
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch {
    // Some engines reject decode() for SVG; the load event is the fallback.
    await new Promise<void>((resolve, reject) => {
      if (img.complete && img.naturalWidth > 0) return resolve();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('cards.exportPng: the SVG would not load'));
    });
  }
  return { img, done: () => URL.revokeObjectURL(url) };
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Draw an SVG to a PNG blob of exactly width x height (the sessions recap is 1080x1350). Fonts and
 * images are inlined as data URIs, no `foreignObject`, no filters, so Safari draws it right.
 * Accepts the element or its markup.
 */
export async function exportPng(svg: SVGSVGElement | string, opts: ExportPngOptions): Promise<Blob> {
  const { width, height } = opts;
  const raw = typeof svg === 'string' ? svg : new XMLSerializer().serializeToString(svg);

  // Pictures first, then the fonts for the text that is in there.
  const hrefs = findImageHrefs(raw);
  const pairs = await Promise.all(hrefs.map(async (h) => [h, await cachedDataUriFor(h)] as const));
  const inlined = inlineImageHrefs(raw, new Map(pairs));
  const subsets: Subset[] = subsetsFor(textOf(inlined));
  const css = await fontFaceCss(subsets, cachedDataUri);
  const markup = prepareSvgMarkup(inlined, { width, height, css });

  const { img, done } = await decodeSvg(markup);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('cards.exportPng: no 2D canvas');
    ctx.drawImage(img, 0, 0, width, height);
    await sleep(60);
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('cards.exportPng: the canvas gave no PNG'))),
        'image/png',
      );
    });
  } finally {
    done();
  }
}

/** Object and blob URLs are one-shot, so only http(s) and root-relative files go through the cache. */
function cachedDataUriFor(url: string): Promise<string> {
  return url.startsWith('blob:') ? fileToDataUri(url) : cachedDataUri(url);
}

/** The figure of the export: a 1100 px sticker from the cutout when there is one. */
async function exportFigure(props: PlayerCardProps): Promise<FigureArt> {
  const art = props.portrait !== undefined ? props.portrait : props.player.portrait;
  const plan = planArt(art, 'l');
  const cutout = art?.cutoutUrl ?? null;
  if (cutout && (plan.kind === 'bake' || plan.kind === 'sticker' || plan.kind === 'measure')) {
    try {
      const { blob, layout } = await buildSticker(cutout, EXPORT_STICKER_H);
      return { kind: 'sticker', href: await blobToDataUri(blob), layout };
    } catch {
      // The cutout would not load: fall back to the stored sticker below.
    }
  }
  if (plan.kind === 'sticker') return plan;
  if (plan.kind === 'measure') return { kind: 'sticker', href: plan.href, layout: await measureSticker(plan.href) };
  if (plan.kind === 'bake') return art?.artUrl ? { kind: 'window', href: art.artUrl } : { kind: 'mannequin' };
  return plan;
}

/**
 * The player card as a 1080x1512 PNG (EXPORT in card-geometry.ts). A card with a cutout gets its
 * sticker regenerated at 1100 px, so the 2.16x export never upscales; one with only a finished
 * sticker uses that file; the rest (art without a cutout, the mannequin) go as they are.
 */
export async function exportCardPng(props: PlayerCardProps): Promise<Blob> {
  const { renderToStaticMarkup } = await import('react-dom/server');
  const { player, rating } = props;
  const art = props.portrait !== undefined ? props.portrait : player.portrait;
  const figure = await exportFigure(props);
  const state = cardState(rating);
  const tier = cardTier(rating);
  const preset = props.preset ?? art?.preset ?? 'rookie_card';
  const markup = renderToStaticMarkup(
    createElement(CardSvg, {
      size: 'l',
      uid: 'x',
      title: cardAriaLabel(player, rating),
      name: plateName(player),
      jersey: jerseyText(player.jersey),
      shownOvr: cardOvr(rating),
      tier,
      tierTag: tierLabel(tier),
      stateTag: state === 'provisional' ? copy.provisionalTag : state === 'rusty' ? copy.rustyTag : null,
      stateTagShort: null,
      stateFill: state === 'rusty' ? '#ffc299' : '#ffffa1',
      ribbon: copy.ribbon[preset],
      subline: subline(rating, player.id, Date.now()),
      attrs: largeAttrs(rating),
      preset,
      figure,
      nudge: art?.nudge ?? null,
      flags: revealFlags(undefined),
      shadowColor: TIER_SHADOW,
      animate: false,
      singleDigitTitle: copy.singleDigits,
    }),
  );
  return exportPng(markup, CARD_EXPORT_SIZE);
}
